import {
	useState,
	useRef,
	useEffect,
	forwardRef,
	FocusEvent,
	ChangeEvent,
	KeyboardEvent
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Datepicker.module.scss';
import { Text, Caption, Spacer } from '@components';
import { useDatePicker, DPDay } from '@rehookify/datepicker';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import CalendarIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import formatDate from 'date-fns/format';
import {
	VALIDATION_ERROR,
	PartialInputEvent,
	TObject,
	TEXT_WEIGHT,
	BUTTON_ICON_POS,
	DYNAMIC_DAYS
} from '@types';
import { ZodObject } from 'zod';
import { Scrollbar } from '@components';
import { timeStringToMinutes } from '@utils';

type DatepickerProps = {
	id?: string;
	closeOnSelect?: boolean;
	disabled?: boolean;
	disableFuture?: boolean;
	disablePast?: boolean;
	label?: string;
	maxDate?: Date;
	minDate?: Date;
	onChange: (e:Date | string | PartialInputEvent | Date[] | string[]) => void;
	open?: boolean;
	value: Date | string | Date[] | string[];
	autoFocus?: boolean;
	onOpen?: () => void;
	onClose?: () => void;
	dateOnly?: boolean;
	timeOnly?: boolean;
	placeholder?: string;
	format?: string;
	canAcceptReject?: boolean;
	onAccept?: () => void;
	onReject?: () => void;
	required?: boolean;
	stretch?: boolean;
	onBlur?: (e?:FocusEvent<HTMLInputElement>) => void;
	tabIndex?: number;
	displayThin?: boolean;
	onFocus?: () => void;
	useLabelSpace?: boolean;
	name: string;
	returnNotSynthetic?: boolean;
	returnAsFormatedString?: boolean;
	hasError?: boolean;
	errorMessage?: string;
	onValidationError?: (errors:VALIDATION_ERROR, clear:boolean) => void;
	onValidationSuccess?: (errors:VALIDATION_ERROR, clear:boolean) => void;
	keepErrorSpace?: boolean;
	validations?: ZodObject<any>;
	iconPos?: BUTTON_ICON_POS
	readOnly?: boolean;
	hasDynamicDays?: boolean;
	shouldDisableDate?: (date: Date) => boolean;
	minTime?: string;
	maxTime?: string;
	visibleTimeStart?: string;
	visibleTimeEnd?: string;
}

/* Height of a single hour/minute row in the time panel, used to scroll the selection into view. */
const TIME_ROW_HEIGHT = 25;
const MINS_IN_DAY = 24 * 60;

type DTParts = {
	empty: boolean;
	theDay: string;
	theMonth: string;
	theYear: string;
	theHour: string;
	theMin: string;
}

const CoreDatepicker = forwardRef((props:DatepickerProps, _ref) => {
	const {
		id = 'date-picker',
		closeOnSelect = true,
		disabled = false,
		disableFuture = false,
		disablePast = false,
		label,
		maxDate,
		minDate,
		onChange,
		open,
		value,
		autoFocus = false,
		onOpen,
		onClose,
		dateOnly = false,
		timeOnly = false,
		placeholder,
		format,
		canAcceptReject = false,
		onAccept,
		onReject,
		required = false,
		stretch = false,
		onBlur,
		tabIndex,
		displayThin = false,
		onFocus,
		useLabelSpace,
		name,
		returnNotSynthetic,
		returnAsFormatedString = false,
		hasError,
		errorMessage,
		onValidationError,
		onValidationSuccess,
		keepErrorSpace = true,
		validations,
		iconPos = 'RIGHT',
		readOnly = false,
		hasDynamicDays = false,
		shouldDisableDate,
		minTime,
		maxTime,
		visibleTimeStart,
		visibleTimeEnd
	} = props;

	const pickerRef = useRef<HTMLDivElement>(null);
	const pickerInputRef = useRef<HTMLDivElement>(null);
	const calRef = useRef<HTMLDivElement>(null);
	const calContainerRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLDivElement>(null);
	const hourRef = useRef<HTMLButtonElement>(null);
	const minRef = useRef<HTMLButtonElement>(null);
	const hourContainerRef = useRef<HTMLDivElement>(null);
	const minContainerRef = useRef<HTMLDivElement>(null);
	const dateInputRef = useRef<HTMLInputElement>(null);
	const timeInputRef = useRef<HTMLInputElement>(null);
	const isTouched = useRef<boolean>(false);
	const [isActive, setIsActive] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<'date' | 'time'>(timeOnly ? 'time' : 'date');
	const [isAboveFold, setIsAboveFold] = useState<boolean>(true);
	const [calHeight, setCalHeight] = useState<number>(0);
	const [pickerVal, setPickerVal] = useState<string | string[]>('');
	const [timePickerVal, setTimePickerVal] = useState<string | string[]>('');
	const [isOpen, setIsOpen] = useState<boolean | undefined>(open);
	const [selectedDates, onDatesChange] = useState<Date[]>([]);
	const [selectedDynamicDay, setSelectedDynamicDay] = useState('');
	const [, setAutoFocusField] = useState<boolean>(autoFocus);
	const [hourVal, setHourVal] = useState<string | string[]>('');
	const [minVal, setMinVal] = useState<string | string[]>('');
	const [containerPos, setContainerPos] = useState<DOMRect | null>(null);
	const [showWidget, setShowWidget] = useState<string>('');
	const [showMonthPanel, setShowMonthPanel] = useState<boolean>(false);
	const [showYearPanel, setShowYearPanel] = useState<boolean>(false);
	const [offsetDate, onOffsetChange] = useState<Date>(new Date());
	const [touched, setTouched] = useState<boolean>(false);
	const fieldFocus = useRef<number>(-1);
	/* Without minTime/maxTime the window spans the whole day, so nothing below is disabled. */
	const windowStart = minTime ? timeStringToMinutes(minTime) : 0;
	const windowEnd = maxTime ? timeStringToMinutes(maxTime) : MINS_IN_DAY - 1;
	const clampToWindow = (mins: number) => Math.min(Math.max(mins, windowStart), windowEnd);
	const isOutOfWindow = (mins: number) => mins < windowStart || mins > windowEnd;
	const selectedHour = parseInt(`${hourVal}`, 10);
	/*
		Which hours the list offers at all, as opposed to minTime/maxTime, which say which of them
		can be picked. The two differ when the selectable window narrows inside a fixed set of
		hours — a same-day booking loses the morning as it goes, but the hours a location opens for
		do not change, so they stay on show and go grey. Hours the location never opens for are
		dropped instead: a row that can never be picked on any day is just noise.
	*/
	const listStart = visibleTimeStart ? timeStringToMinutes(visibleTimeStart) : 0;
	const listEnd = visibleTimeEnd ? timeStringToMinutes(visibleTimeEnd) : MINS_IN_DAY - 1;
	const firstHour = Math.floor(listStart / 60);
	const lastHour = Math.max(Math.floor(listEnd / 60), firstHour);
	const hours = [...Array(lastHour - firstHour + 1).keys()].map((row) => row + firstHour);
	/* An hour stays selectable while any minute inside it falls within the window. */
	const isHourDisabled = (hour: number) => hour * 60 + 59 < windowStart || hour * 60 > windowEnd;
	const isMinDisabled = (min: number) => !isNaN(selectedHour) && isOutOfWindow(selectedHour * 60 + min);
	const allMins = [...Array(60).keys()];
	const minsInWindow = allMins.filter((min) => !isMinDisabled(min));
	/*
		Minutes are dropped rather than greyed, unlike the hours: an hour carries 60 of them, so the
		last hour of the window would otherwise offer :00 above a column of 59 rows nobody can pick.
		An hour wholly outside the window has none to offer, and an empty panel says less than a full
		one that cannot be used, so there the whole set stays on show and greys out instead.
	*/
	const mins = minsInWindow.length > 0 ? minsInWindow : allMins;
	const [firstMin] = mins;
	/*
		Where a value sits in its panel. Either list can start part-way through its range, so the
		row is counted from the first one on show rather than from the value itself.
	*/
	const scrollTopFor = (val: string | number | undefined, firstRow: number = 0) => {
		const row = parseInt(`${val}`, 10);
		return isNaN(row) ? 0 : Math.max(row - firstRow, 0) * TIME_ROW_HEIGHT;
	};

	const inputProps = () => {
		const newProps = { ...props };
		delete newProps.closeOnSelect;
		delete newProps.disableFuture;
		delete newProps.disablePast;
		delete newProps.maxDate;
		delete newProps.minDate;
		delete newProps.autoFocus;
		delete newProps.onOpen;
		delete newProps.onClose;
		delete newProps.dateOnly;
		delete newProps.timeOnly;
		delete newProps.format;
		delete newProps.canAcceptReject;
		delete newProps.onAccept;
		delete newProps.onReject;
		delete newProps.stretch;
		delete newProps.displayThin;
		delete newProps.useLabelSpace;
		delete newProps.returnNotSynthetic;
		delete newProps.returnAsFormatedString;
		delete newProps.hasError;
		delete newProps.errorMessage;
		delete newProps.onValidationError;
		delete newProps.onValidationSuccess;
		delete newProps.keepErrorSpace;
		delete newProps.validations;
		delete newProps.iconPos;
		delete newProps.hasDynamicDays;
		delete newProps.shouldDisableDate;
		delete newProps.minTime;
		delete newProps.maxTime;
		delete newProps.visibleTimeStart;
		delete newProps.visibleTimeEnd;
		return newProps;
	}

	const {
		data: { calendars, weekDays, formattedDates, months, years },
		propGetters: {
			addOffset,
			subtractOffset
		}
	} = useDatePicker({
		selectedDates,
		onDatesChange,
		offsetDate,
		onOffsetChange,
		dates: {
			toggle: true,
			mode: 'multiple',
			minDate: disablePast ? new Date(new Date()) : minDate,
			maxDate: disableFuture ? new Date(new Date()) : maxDate
		},
		calendar: { offsets: [1] },
		locale: {
			hour: '2-digit',
			minute: '2-digit',
			weekday: 'narrow',
			monthName: 'short'
		}
	});
	const [{ month, year, days }] = calendars;
	const buildDayClasses = (day:DPDay) => {
		const classes = [styles.day];
		day.inCurrentMonth ? classes.push(styles.currentMonthDay) : classes.push(styles.nonMonthDay);
		if (day.now) {
			classes.push(styles.highlightToday);
		}
		if (day.selected) {
			classes.push(styles.selectedDay);
		}
		return classes.join(' ');
	}
	const dateTimeParts = (t: 'date' | 'time', index: number | null = null): DTParts => {
		const now = new Date();
		if (
			(`${pickerVal}` === '' && `${timePickerVal}` === '') ||
			(pickerVal[0] === '' && pickerVal[1] === '') ||
			(timePickerVal[0] === '' && timePickerVal[1] === '')
		) {
			return {
				empty: true,
				theDay: '',
				theMonth: '',
				theYear: '',
				theHour: '',
				theMin: ''
			}
		}
		if (t === 'date') {
			const [theDay, theMonth, theYear] = index ? pickerVal[index].split('/') : `${pickerVal}`.split('/');
			return {
				empty: false,
				theDay: theDay ? theDay : '',
				theMonth: theMonth ? theMonth : '',
				theYear: theYear ? theYear : '',
				theHour: '',
				theMin: ''
			};
		} else if (t === 'time') {
			const [theHour, theMin] = index ? (timePickerVal[index] ?? '').split(':') : `${timePickerVal}`.split(':');
			return {
				empty: false,
				theDay: '',
				theMonth: '',
				theYear: '',
				theHour: theHour ? theHour : '',
				theMin: theMin ? theMin : ''
			};
		} else {
			const [theDay, theMonth, theYear] = (index ? pickerVal[index] : pickerVal as string ?? []).split('/');
			const [theHour, theMin] = timePickerVal ? (index ? timePickerVal[index] : timePickerVal as string ?? []).split(':') : ['00', '00'];
			return {
				empty: false,
				theDay: theDay ? theDay : (now.getDate() <= 9 ? `0${now.getDate()}` : `${now.getDate()}`),
				theMonth: theMonth ? theMonth : (now.getMonth() <= 9 ? `0${now.getMonth() + 1}` : `${now.getMonth() + 1}`),
				theYear: theYear ? theYear : `${now.getFullYear()}`,
				theHour,
				theMin
			};
		}
	}
	const updatePicker = (part: 'date' | 'time', index: number | null = null) => {
		if ((index === null && part === 'date') || (index !== null && index >= 0)) {
			const parts = dateTimeParts('date', index);
			if (!parts.empty) {
				const timeParts = dateTimeParts('time', index);
				const now = new Date();
				let theDate = new Date(
					isNaN(parseInt(`${parts.theYear}`)) ? now.getFullYear() : parseInt(`${parts.theYear}`),
					isNaN(parseInt(`${parts.theMonth}`)) ? now.getMonth() - 1 : parseInt(`${parts.theMonth}`) - 1,
					isNaN(parseInt(`${parts.theDay}`)) ? now.getDate() : parseInt(`${parts.theDay}`)
				);
				if (!timeParts.empty) {
					theDate = new Date(
						isNaN(parseInt(`${parts.theYear}`)) ? now.getFullYear() : parseInt(`${parts.theYear}`),
						isNaN(parseInt(`${parts.theMonth}`)) ? now.getMonth() - 1 : parseInt(`${parts.theMonth}`) - 1,
						isNaN(parseInt(`${parts.theDay}`)) ? now.getDate() : parseInt(`${parts.theDay}`),
						isNaN(parseInt(`${timeParts.theHour}`)) ? now.getHours() : parseInt(`${timeParts.theHour}`),
						isNaN(parseInt(`${timeParts.theMin}`)) ? now.getMinutes() : parseInt(`${timeParts.theMin}`),
					);
				}
				onDatesChange([theDate]);
			}
		} else if ((index === null && part === 'time') || (index !== null && index >= 0)) {
			const parts = dateTimeParts('time', index);
			setHourVal(parts.theHour);
			setMinVal(parts.theMin);
			setTimeout(() => {
				if (hourContainerRef.current) {
					hourContainerRef.current.scrollTo({ top: scrollTopFor(parts.theHour, firstHour) });
				}
				if (minContainerRef.current) {
					minContainerRef.current.scrollTo({ top: scrollTopFor(parts.theMin, firstMin) });
				}
			}, 0);
		}
	}
	const updatePickerPos = () => {
		if (pickerInputRef?.current) {
			setContainerPos(pickerInputRef?.current.getBoundingClientRect());
		}
	}
	const toggleDPOpen = (b:boolean, part: 'date' | 'time') => {
		fieldFocus.current = part === 'date' ? 0 : 1;
		updatePickerPos();
		setIsActive(b);
		if (pickerRef.current) {
			setIsAboveFold(window.innerHeight - pickerRef.current.getBoundingClientRect().y - pickerRef.current.offsetHeight - 300 > 0);
		}
		setIsOpen(b);
		if (onOpen && b) onOpen();
		if (onClose && !b) onClose();
		if (b) updatePicker(part, null);
		part === 'date' ? setActiveTab('date') : setActiveTab('time');
	}
	const withLeading0 = (t:number | string | undefined) => {
		if (!t) return '00'
		return `${t}`.length === 1 ? `0${t}` : t;
	}
	const updateTime = (t:string, b:boolean) => {
		b ? setHourVal(t) : setMinVal(t);
		if (timeOnly) {
			let newTime;
			if (pickerVal === '') {
				newTime = b ? `${withLeading0(t)}:${withLeading0(minVal as string) || '00'}` : `${withLeading0(hourVal as string) || '00'}:${withLeading0(t)}`;
			} else {
				const [hour, min] = `${pickerVal}`.split(':');
				newTime = b ? `${withLeading0(t)}:${min || '00'}` : `${hour || '00'}:${withLeading0(t)}`;
			}
			const [h, m] = newTime.split(':').map(Number);
			const clamped = clampToWindow(h * 60 + m);
			if (clamped !== h * 60 + m) {
				const clampedHour = Math.floor(clamped / 60);
				const clampedMin = clamped % 60;
				newTime = `${withLeading0(`${clampedHour}`)}:${withLeading0(`${clampedMin}`)}`;
				setHourVal(`${clampedHour}`);
				setMinVal(`${clampedMin}`);
			}
			setTimePickerVal(newTime);
			if (returnNotSynthetic) {
				onChange(newTime);
			} else {
				const timeParts = newTime.split(':');
				const now = new Date();
				const retObj:PartialInputEvent = {
					target: {
						name: name,
						value: returnAsFormatedString ? newTime : new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), parseInt(timeParts[0]), parseInt(timeParts[1]))
					}
				}
				onChange(retObj);
			}
		} else {
			const [theDate] = selectedDates;
			b ? theDate.setHours(+t) : theDate.setMinutes(+t);
			onDatesChange([theDate]);
		}
		if (!isTouched.current) isTouched.current = true;
	}
	const dateFormatter = (v:string) => {
		const cleaned = (`${v}`).replace(/\D/g, '');
		const match = cleaned.match(/^(\d{0,2})?(\d{0,2})?(\d{0,4})?/);
		if (match) {
			return [
				match[1],
				match[2] ? '/': '',
				match[2],
				match[3] ? '/': '',
				match[3]
			].join('');
		}
		return v;
	}
	const timeFormatter = (v:string) => {
		let cleaned = (`${v}`).replace(/\D/g, '');
		if (parseInt(cleaned.charAt(0)) >= 3) {
			cleaned = `0${cleaned}`;
		}
		const match = cleaned.match(/^(\d{0,2})?(\d{0,2})?/);
		if (match) {
			return [
				match[1],
				match[2] ? ':' : '',
				match[2]
			].join('');
		}
		return v;
	}
	const returnDateTime = () => {
		const retVal = `${pickerVal} ${timePickerVal}`;
		returnNotSynthetic ? onChange(retVal) : onChange({
			target: {
				name,
				value: retVal
			}
		});
	}
	const updatePickerVal = (e:ChangeEvent<HTMLInputElement>, index: number | null = null) => {
		const formatted = dateFormatter(e.target.value);
		setPickerVal(formatted);
		if (dateOnly) {
			returnNotSynthetic ? onChange(formatted) : onChange(e);
		} else {
			returnDateTime();
		}
		if (e.target.value === '') {
			onDatesChange([]);
		}
		if (formatted.length === 10) {
			const dateParts = formatted.split('/');
			onDatesChange([new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]))]);
			if ((!dateOnly || index === 0) && timeInputRef.current) {
				timeInputRef.current.focus();
				setActiveTab('time');
			}
		}
	}
	const updateTimePickerVal = (e:ChangeEvent<HTMLInputElement>) => {
		const formatted = timeFormatter(e.target.value);
		const timeParts = formatted.split(':');
		setHourVal(`${withLeading0(timeParts[0])}`);
		setMinVal(`${withLeading0(timeParts[1])}`);
		setTimePickerVal(formatted);
		if (timeOnly) {
			returnNotSynthetic ? onChange(formatted) : onChange(e);
		} else {
			returnDateTime();
		}
		if (hourContainerRef.current) {
			hourContainerRef.current.scrollTo({ top: scrollTopFor(timeParts[0], firstHour) });
		}
		if (minContainerRef.current) {
			minContainerRef.current.scrollTo({ top: scrollTopFor(timeParts[1], firstMin) });
		}
		if (!isTouched.current) isTouched.current = true;
		if (fieldFocus.current === 0 && timeInputRef.current && formatted.length === 5) {
			fieldFocus.current = 1;
			timeInputRef.current.focus();
		}
	}
	const switchTab = (part: 'date' | 'time') => {
		setActiveTab(part);
		if (part === 'time') {
			setTimeout(() => {
				if (hourContainerRef.current) {
					hourContainerRef.current.scrollTo({ top: scrollTopFor(hourVal as string, firstHour) });
				}
				if (minContainerRef.current) {
					minContainerRef.current.scrollTo({ top: scrollTopFor(minVal as string, firstMin) });
				}
			}, 0);
		}
	}
	const triggerClose = () => {
		setTimeout(() => {
			setIsOpen(false);
			if (onClose) onClose();
		}, 0);
	}
	const updateDynamicDay = (d: DYNAMIC_DAYS) => {
		isTouched.current = true;
		setSelectedDynamicDay(d);
		onDatesChange([]);
		if (closeOnSelect) {
			triggerClose();
		}
	}
	const updateDates = (d:Date) => {
		isTouched.current = true;
		onDatesChange([d]);
		setSelectedDynamicDay('');
		if (closeOnSelect && (dateOnly || timeOnly)) {
			triggerClose();
		}
		if (!dateOnly) {
			switchTab('time');
			timeInputRef.current?.focus();
		}
	}
	const setPos = () => {
		const styles:TObject = {}
		if (!isAboveFold) {
			if (calContainerRef?.current && containerPos) {
				styles.transform = `translate3d(${Math.round(containerPos.x)}px,${Math.round(containerPos.y - calContainerRef.current.offsetHeight)}px,0px)`;
			} else if (containerPos) {
				styles.transform = `translate3d(${Math.round(containerPos.x)}px,${Math.round(containerPos.y - 305)}px,0px)`;
			}
		} else if (containerPos) {
			styles.transform = `translate3d(${Math.round(containerPos.x)}px,${Math.round(containerPos.y + containerPos.height)}px,0px)`;
		}
		return styles;
	}
	const setPlaceholder = (part: 'date' | 'time') => {
		if (placeholder && (dateOnly || timeOnly)) {
			return placeholder;
		}
		if (part === 'date') {
			return 'dd/mm/yyyy';
		}
		if (part === 'time') {
			return 'HH:MM';
		}
	}
	const checkValidations = () => {
		if (validations) {
			const valid = validations.safeParse(value);
			if (!valid.success) {
				const issues = [];
				for (let i=0, l=valid.error.issues.length; i<l; ++i) {
					issues.push(valid.error.issues[i].message);
				}
				const error = {
					field: name,
					issues
				};
				if (onValidationError) {
					onValidationError(error, false);
				}
			} else if (onValidationSuccess) {
				onValidationSuccess({ field: name }, true);
			}
		}
	}
	const triggerBlur = (e:FocusEvent<HTMLInputElement>) => {
		checkValidations();
		if (onBlur) onBlur(e);
	}
	const triggerFocus = () => {
		if (onFocus) onFocus();
		if (!touched) setTouched(true);
	}
	const onDateKeyDown = (e:KeyboardEvent<HTMLInputElement>) => {
		if ((dateOnly && e.key === 'Tab') || (e.shiftKey && e.key === 'Tab')) {
			setIsOpen(false);
			setIsActive(false);
		}
	}
	const onTimeKeyDown = (e:KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Tab') {
			setIsOpen(false);
			setIsActive(false);
		}
		if (!timeOnly && dateInputRef.current) {
			if (timePickerVal.length <= 1 && e.key === 'Backspace') {
				setTimeout(() => {
					dateInputRef?.current?.focus();
				}, 0);
			}
		}
	}
	const handleClickOutside = (e:MouseEvent) => {
		if (isOpen) {
			if (!pickerRef?.current?.contains(e.target as Node) && !calContainerRef?.current?.contains(e.target as Node)) {
				setIsOpen(false);
				setIsActive(false);
				if (onClose) {
					onClose();
				}
			}
		}
	}
	const toggleDatePanelDisplay = () => {
		if (!showMonthPanel && !showYearPanel) setShowMonthPanel(true);
		if (showMonthPanel && !showYearPanel) {
			setShowMonthPanel(false);
			setShowYearPanel(true);
		}
	}
	const getSelectableYearRange = () => {
		const thisYear = new Date().getFullYear();
		const firstYear = thisYear - 6;
		const lastYear = thisYear + 5;
		return `${firstYear} - ${lastYear}`;
	}
	const setNewYear = (year:number) => {
		onOffsetChange(new Date(year, offsetDate.getMonth(), offsetDate.getDate()));
		setShowYearPanel(false);
		setShowMonthPanel(true);
	}
	const setNewMonth = (month:string) => {
		const monthIndex = months.findIndex((m) => m.month === month);
		onOffsetChange(new Date(offsetDate.getFullYear(), monthIndex, offsetDate.getDate()));
		setShowYearPanel(false);
		setShowMonthPanel(false);
	}
	const isHourMin = (type: 'hour' | 'min', checkVal: string | number, index:number): boolean => {
		if (type === 'hour') {
			if (hourVal === '') return false;
			const isString = typeof hourVal === 'string' && hourVal.indexOf(',') < 0;
			return withLeading0(
				isString ? hourVal as string :
					hourVal.indexOf(',') < 0 ? hourVal[fieldFocus.current] :
						(hourVal as string).split(',')[index]
			) === withLeading0(`${checkVal}`);
		} else {
			if (minVal === '') return false;
			const isString = typeof minVal === 'string' && minVal.indexOf(',') < 0;
			return withLeading0(
				isString ? minVal as string :
					minVal.indexOf(',') < 0 ? minVal[fieldFocus.current] :
						(minVal as string).split(',')[index]
			) === withLeading0(`${checkVal}`);
		}
	}
	const isDynamicDay = (value: string) => {
		return Object.values(DYNAMIC_DAYS).find((d) => d.toLowerCase() === value.toLowerCase())
	}

	useEffect(() => {
		if (selectedDates.length > 0) {
			let [theDate1] = formattedDates;
			if (!dateOnly) {
				theDate1 = `${theDate1} ${(withLeading0(hourVal as string) || '00')}:${(withLeading0(minVal as string) || '00')}`;
			}
			if (returnNotSynthetic) {
				onChange(theDate1);
			} else {
				const retObj:PartialInputEvent = {
					target: {
						name: name,
						value: returnAsFormatedString ? theDate1 : new Date(selectedDates[0])
					}
				}
				onChange(retObj);
			}
		}
		if (selectedDynamicDay !== '') {
			if (returnNotSynthetic) {
				onChange(selectedDynamicDay);
			} else {
				const retObj:PartialInputEvent = {
					target: {
						name: name,
						value: selectedDynamicDay
					}
				}
				onChange(retObj);
			}
		}
	}, [selectedDates, selectedDynamicDay]);
	useEffect(() => {
		if (isOpen) {
			document.removeEventListener('mousedown', handleClickOutside);
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}
		document.addEventListener('mousedown', handleClickOutside);
		return (() => {
			document.removeEventListener('mousedown', handleClickOutside);
		});
	}, [pickerRef, isOpen]);
	useEffect(() => {
		if (autoFocus) {
			setAutoFocusField(autoFocus);
		}
	}, [autoFocus]);

	useEffect(() => {
		if (calRef?.current) {
			setCalHeight(calRef?.current.offsetHeight);
		}
	});
	useEffect(() => {
		if (isOpen) {
			updatePickerPos();
		}
		if (!dateOnly) {
			if (hourContainerRef && hourContainerRef.current && hourRef && hourRef.current) {
				hourContainerRef.current.scrollTop = (hourRef?.current?.getBoundingClientRect().y - hourContainerRef?.current?.getBoundingClientRect().y) - 100;
			}
			if (minContainerRef && minContainerRef.current && minRef && minRef.current) {
				minContainerRef.current.scrollTop = (minRef?.current?.getBoundingClientRect().y - minContainerRef?.current?.getBoundingClientRect().y) - 100;
			}
		}
		if (!timeOnly) {
			const now = new Date();
			const nowYear = now.getFullYear();
			const nowMonth = now.getMonth();
			const nowDate = now.getDate();
			if (pickerVal === '') {
				onOffsetChange(new Date(nowYear, nowMonth, nowDate));
			} else {
				const parts = dateTimeParts('date');
				onOffsetChange(new Date(
					parseInt(`${parts.theYear}`),
					parseInt(`${parts.theMonth}`) - 1,
					parseInt(`${parts.theDay}`)
				));
			}
		}
		const timeout = setTimeout(() => {
			if (isOpen) {
				setShowWidget(styles.showMe);
			} else {
				setShowWidget('');
			}
		}, 250);
		if (!touched && isOpen) setTouched(true);
		return (() => {
			clearTimeout(timeout);
		});
	}, [isOpen]);
	useEffect(() => {
		if (value instanceof Date) {
			if (!dateOnly && !timeOnly) {
				setPickerVal(formatDate(value, format ?? 'dd/MM/yyyy'));
				if (isTouched.current) setTimePickerVal(formatDate(value, format ?? 'HH:mm'));
			} else if (dateOnly) {
				setPickerVal(formatDate(value, format ?? 'dd/MM/yyyy'));
			} else if (timeOnly) {
				setTimePickerVal(formatDate(value, format ?? 'HH:mm'));
			}
		} else if (typeof value === 'string' && value === '') {
			setPickerVal('');
			setTimePickerVal('');
			setSelectedDynamicDay('');
		} else if (typeof value === 'string' && value !== '') {
			const dynamicDay = isDynamicDay(value);
			if (dynamicDay !== undefined) {
				setSelectedDynamicDay(dynamicDay);
				setPickerVal('');
				setTimePickerVal('');
			} else {
				setSelectedDynamicDay('');
				if (timeOnly) {
					const formatted = timeFormatter(value);
					const timeParts = formatted.split(':');
					setHourVal(`${withLeading0(timeParts[0])}`);
					setMinVal(`${withLeading0(timeParts[1])}`);
					setTimePickerVal(formatted);
				}
				if (dateOnly) setPickerVal(value);
			}
		}
		isTouched.current && checkValidations();
	}, [value]);
	useEffect(() => {
		const updateOnScroll = () => {
			updatePickerPos();
		}
		window.addEventListener('scroll', updateOnScroll);
		return (() => {
			window.removeEventListener('scroll', updateOnScroll);
		});
	}, []);

	return (
		<div data-testid={'@Ofload-Datepicker'} className={`${styles.container} ${stretch ? styles.flex : ''}`} ref={pickerRef} onClick={() => updatePickerPos()}>
			<div className={styles.labelsContainer}>
				{label && <label className={styles.label}>{label}{required ? <span className={styles.required}> *</span> : <></>}</label>}
				{!label && useLabelSpace && <label className={styles.labelTransparent} htmlFor={id}>{name}{required ? <span className={styles.required}> *</span> : <></>}</label>}
			</div>
			<div className={`${styles.inputContainer} ${hasError ? styles.hasError : ''} ${displayThin ? styles.displayThin : ''} ${isActive ? styles.activeInput : ''} ${disabled ? styles.disabledInput : ''}`} ref={pickerInputRef}>
				{iconPos === 'LEFT' && !timeOnly && <CalendarIcon data-testid={'@DTP-Cal-Icon-Left'} className={`${styles.inputIcon} ${disabled ? styles.disabledIcon : ''} ${styles.inputIconLeft}`} onClick={() => { !disabled && toggleDPOpen(!isOpen, 'date') }} />}
				{iconPos === 'LEFT' && timeOnly && <AccessTimeIcon data-testid={'@DTP-Time-Icon-Left'} className={`${styles.inputIcon} ${disabled ? styles.disabledIcon : ''} ${styles.inputIconLeft}`} onClick={() => { !disabled && toggleDPOpen(!isOpen, 'time') }} />}
				{!timeOnly && <input
					{...inputProps()}
					className={`${styles.input} ${!dateOnly && styles.multiInput}`}
					type={'text'}
					onChange={(e:ChangeEvent<HTMLInputElement>) => updatePickerVal(e) }
					value={ pickerVal || selectedDynamicDay }
					onFocus={() => toggleDPOpen(true, 'date')}
					disabled={disabled}
					ref={dateInputRef}
					placeholder={setPlaceholder('date')}
					onBlur={(e) => triggerBlur(e)}
					tabIndex={tabIndex}
					onKeyDown={(e) => onDateKeyDown(e)}
					onFocusCapture={() => triggerFocus()}
					name={name}
					autoComplete='off'
					readOnly={readOnly}
				/>}
				{!dateOnly && <input
					{...inputProps()}
					className={styles.input}
					type={'text'}
					onChange={(e:ChangeEvent<HTMLInputElement>) => updateTimePickerVal(e) }
					value={ timePickerVal }
					onFocus={() => toggleDPOpen(true, 'time')}
					disabled={disabled}
					ref={timeInputRef}
					placeholder={setPlaceholder('time')}
					onBlur={(e) => triggerBlur(e)}
					tabIndex={tabIndex ? tabIndex + 1 : tabIndex}
					onKeyDown={(e) => onTimeKeyDown(e)}
					onFocusCapture={() => triggerFocus()}
					name={name}
					autoComplete='off'
				/>}
				{canAcceptReject && <>
					<button className={`${styles.acceptRejectBtn} ${styles.accept}`} onClick={() => { if (onAccept) onAccept() }} aria-label={`Datepicker Accept Button`}><DoneIcon /></button>
					<button className={`${styles.acceptRejectBtn} ${styles.reject}`} onClick={() => { if (onReject) onReject() }} aria-label={`Datepicker Reject Button`}><ClearIcon /></button>
				</>}
				{iconPos === 'RIGHT' && !timeOnly && <CalendarIcon data-testid={'@DTP-Cal-Icon-Right'} className={`${styles.inputIcon} ${disabled ? styles.disabledIcon : ''}`} onClick={() => { !disabled && toggleDPOpen(!isOpen, 'date') }} />}
				{iconPos === 'RIGHT' && timeOnly && <AccessTimeIcon data-testid={'@DTP-Time-Icon-Right'} className={`${styles.inputIcon} ${disabled ? styles.disabledIcon : ''}`} onClick={() => { !disabled && toggleDPOpen(!isOpen, 'time') }} />}
			</div>
			{hasError && <div className={styles.errorMsgContainer}>
				<Spacer size={5} />
				<Caption color={'var(--txt-error)'}>
					{errorMessage}
				</Caption>
			</div>}
			{keepErrorSpace && !hasError && <div className={styles.errorMsgContainer} />}
			{isOpen && createPortal(<div data-testid={'@Ofload-Datepicker-Widget'} className={`${styles.calFullContainer} ${showWidget}`} style={setPos()}>
				<div className={`${styles.calContainer}`} ref={calContainerRef}>
					{hasDynamicDays && <ul className={styles.dynamicDayList}>
						{Object.values(DYNAMIC_DAYS).map((day, i) => {
							return <li key={i}>
								<button className={isDynamicDay(selectedDynamicDay) === day ? styles.active : ''} onClick={() => updateDynamicDay(day)}>{day}</button>
							</li>
						})}
					</ul>}
					<div>
						<header className={styles.calHeader}>
							{!dateOnly && !timeOnly && <div className={styles.dateTimeTabs}>
								<button
									className={`${styles.dateTimeTab} ${activeTab === 'date' ? styles.activeTab : ''}`}
									onClick={() => switchTab('date')}
									aria-label={`Datepicker switch to date`}
								>
									<CalendarMonthIcon style={{ color: 'var--neutral-07)' }} />
								</button>
								<button
									className={`${styles.dateTimeTab} ${activeTab === 'time' ? styles.activeTab : ''}`}
									onClick={() => switchTab('time')}
									aria-label={`Datepicker switch to time`}
								>
									<AccessTimeIcon style={{ color: 'var(--neutral-07)' }} />
								</button>
							</div>}
							{!timeOnly && activeTab === 'date' && <>
								<div className={styles.monthSelector} ref={headerRef}>
									<button {...subtractOffset({ months: 1 })} aria-label={`Datepicker Back 1 month`}>
										<ArrowBack className={styles.arrowIcons} />
									</button>
									<div style={{ flex: 1 }} />
									<button className={`${styles.yearMonthSelector} ${showYearPanel && styles.noSelectorClick}`} onClick={toggleDatePanelDisplay} aria-label={`Datepicker Toggle Selector`}>
										<Text color={'var(--neutral-07)'} weight={TEXT_WEIGHT.SEMI_BOLD}>
											{!showMonthPanel && !showYearPanel && <>{month} {year}</>}
											{showMonthPanel && year}
											{showYearPanel && getSelectableYearRange()}
										</Text>
									</button>
									<div style={{ flex: 1 }} />
									<button {...addOffset({ months: 1 })} aria-label={'Datepicker Forward 1 month'}>
										<ArrowForward className={styles.arrowIcons} />
									</button>
								</div>
								{!showMonthPanel && !showYearPanel && <div className={styles.weekDays}>
									{weekDays.map((day:string, i:number) => {
										return <Caption key={`${month}-${day}-${i}`} text={day} />
									})}
								</div>}
							</>}
							{(!dateOnly && activeTab === 'time') && <div className={styles.timeHeader} style={{ marginTop: timeOnly ? '8px' : '0px' }}>
								<Text text={'Hour'} color={'var(--neutral-07)'} weight={TEXT_WEIGHT.SEMI_BOLD} />
								<Text text={'Minutes'} color={'var(--neutral-07)'} weight={TEXT_WEIGHT.SEMI_BOLD} />
							</div>}
						</header>
						{!timeOnly && activeTab === 'date' && !showMonthPanel && !showYearPanel && <div className={styles.calendar} ref={calRef}>
							{days.map((day:DPDay) => {
								const extraDisabled = shouldDisableDate?.(day.$date) ?? false;
								return <button
									key={day.$date.toDateString()}
									className={buildDayClasses(day)}
									onClick={() => { if (!extraDisabled) updateDates(day.$date) }}
									disabled={day.disabled || extraDisabled}
									aria-label={`Datepicker select Date ${day.day}`}
								>
									{day.day}
								</button>
							})}
						</div>}
						{!timeOnly && activeTab === 'date' && showMonthPanel && <div className={styles.monthYearBlocks}>
							{months.map((month, i) => {
								return <button onClick={() => setNewMonth(month.month)} className={`${styles.monthYearBlock} ${month.now && styles.activeMonthYear}`} key={i} aria-label={`Datepicker select month ${month.month}`}>
									<Text>{month.month}</Text>
								</button>
							})}
						</div>}
						{!timeOnly && activeTab === 'date' && showYearPanel && <div className={styles.monthYearBlocks}>
							{years.map((year, i) => {
								return <button onClick={() => setNewYear(year.year)} className={`${styles.monthYearBlock} ${years[i].now && styles.activeMonthYear}`} key={i} aria-label={`Datepicker select year ${year.year}`}>
									<Text>{year.year}</Text>
								</button>
							})}
						</div>}
						{(!dateOnly && activeTab === 'time') && <div className={styles.timeContainer}>
							<div className={styles.timePanel} style={{ height: `${(calHeight || 250) - 20}px` }} ref={hourContainerRef}>
								<Scrollbar thumbColor={'var(--neutral-04)'} />
								{hours.map((hour) => {
									return <span key={hour}>
										<button
											onClick={() => updateTime(`${hour}`, true)}
											className={`${styles.timeButton} ${isHourMin('hour', hour, fieldFocus.current) ? styles.activeTime : ''} ${new Date().getHours() === hour ? styles.currentTime : ''}`}
											ref={isHourMin('hour', hour, fieldFocus.current) ? hourRef : undefined}
											disabled={isHourDisabled(hour)}
											aria-label={`Datepicker Hour ${hour} button`}
										>
											{withLeading0(hour)}
										</button>
									</span>
								})}
							</div>
							<div className={styles.timePanel} style={{ height: `${(calHeight || 250) - 20}px` }} ref={minContainerRef}>
								<Scrollbar thumbColor={'var(--neutral-04)'} />
								{mins.map((min) => {
									return <span key={min}>
										<button
											onClick={() => updateTime(`${min}`, false)}
											className={`${styles.timeButton} ${isHourMin('min', min, fieldFocus.current) ? styles.activeTime : ''}${new Date().getMinutes() === min ? styles.currentTime : ''}`}
											key={min}
											ref={isHourMin('min', min, fieldFocus.current) ? minRef : undefined}
											disabled={isMinDisabled(min)}
											aria-label={`Datepicker Minute ${min} button`}
										>
											{withLeading0(min)}
										</button>
									</span>
								})}
							</div>
						</div>}
					</div>
				</div>
			</div>, document.body)}
		</div>
	);
});

export const Datetimepicker = forwardRef((props:DatepickerProps, _ref) => {
	return <CoreDatepicker { ...props } ref={_ref} />
});

export const Datepicker = forwardRef((props:DatepickerProps, _ref) => {
	return <CoreDatepicker { ...props } ref={_ref} dateOnly />
});

export const Timepicker = forwardRef((props:DatepickerProps, _ref) => {
	return <CoreDatepicker { ...props } ref={_ref} timeOnly />
});
